import localforage from "localforage";
import type { TransactionRule } from "~/types/global.types";
import type {
  BudgetCategory,
  BudgetCategoryCodeMapping,
  BudgetCategoryGroup,
} from "~/types/budget.types";
import { cashflowCharts } from "~/translations/cashflow/cashflowCharts";
import { readLanguageCookie } from "~/utils/language";
import {
  applyConnectedCardFilter,
  buildBudgetCategoryMap,
  enrichTransactions,
  type OptionInterface,
  type Transaction,
} from "./chartDataFunctions";

/**
 * In-memory copy of the cashflow localforage payload.
 *
 * IndexedDB can't be read during render, so the first visit to /cashflow used
 * to paint zeros and then jump once the cache resolved. This module starts the
 * read as soon as it's imported (and again from the app shell) and keeps the
 * result in memory so the page can paint the cached numbers on the first frame.
 */

export type CashflowCachedData = {
  transactions: Transaction[];
  rules: TransactionRule[];
  budgetGroups: BudgetCategoryGroup[];
  budgetCategories: BudgetCategory[];
  budgetMappings: BudgetCategoryCodeMapping[];
};

export type CashflowCacheSnapshot = {
  ready: boolean;
  data: CashflowCachedData | null;
};

const SERVER_SNAPSHOT: CashflowCacheSnapshot = { ready: false, data: null };

let snapshot: CashflowCacheSnapshot = { ready: false, data: null };
const listeners = new Set<() => void>();

function emptyData(): CashflowCachedData {
  return {
    transactions: [],
    rules: readTransactionRules(),
    budgetGroups: [],
    budgetCategories: [],
    budgetMappings: [],
  };
}

function emit() {
  listeners.forEach((listener) => listener());
}

export function subscribeCashflowClientCache(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getCashflowCacheSnapshot(): CashflowCacheSnapshot {
  return snapshot;
}

export function getServerCashflowCacheSnapshot(): CashflowCacheSnapshot {
  return SERVER_SNAPSHOT;
}

export function readTransactionRules(): TransactionRule[] {
  if (typeof window === "undefined") return [];
  try {
    const parsed = JSON.parse(
      window.localStorage.getItem("transactionRules") || "[]"
    );
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function readSelectedAccounts(): OptionInterface[] {
  if (typeof window === "undefined") return [];
  try {
    const parsed = JSON.parse(
      window.localStorage.getItem("selectedAccounts") || "[]"
    );
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function accountSelectionKey(accounts: OptionInterface[]): string {
  return accounts.map((account) => account.value).join("\n");
}

type EnrichmentQuery = {
  transactions: Transaction[];
  rules: TransactionRule[];
  lang: string;
  expensesCategoryType: string;
  incomeCategoryType: string;
  accountKey: string;
  budgetGroups: BudgetCategoryGroup[];
  budgetCategories: BudgetCategory[];
  budgetMappings: BudgetCategoryCodeMapping[];
};

type PreparedEnrichment = EnrichmentQuery & {
  rulesKey: string;
  enriched: Transaction[];
};

let preparedEnrichment: PreparedEnrichment | null = null;
let prepareGeneration = 0;

function rulesKey(rules: TransactionRule[]): string {
  return JSON.stringify(rules);
}

export function lookupPreparedEnrichment(
  query: EnrichmentQuery
): Transaction[] | null {
  const prepared = preparedEnrichment;
  if (!prepared) return null;
  if (prepared.transactions !== query.transactions) return null;
  if (prepared.rulesKey !== rulesKey(query.rules)) return null;
  if (prepared.lang !== query.lang) return null;
  if (prepared.expensesCategoryType !== query.expensesCategoryType) return null;
  if (prepared.incomeCategoryType !== query.incomeCategoryType) return null;
  if (prepared.accountKey !== query.accountKey) return null;
  if (prepared.budgetGroups !== query.budgetGroups) return null;
  if (prepared.budgetCategories !== query.budgetCategories) return null;
  if (prepared.budgetMappings !== query.budgetMappings) return null;
  return prepared.enriched;
}

export function storePreparedEnrichment(
  query: EnrichmentQuery,
  enriched: Transaction[]
) {
  preparedEnrichment = { ...query, rulesKey: rulesKey(query.rules), enriched };
}

function cloneTransaction(transaction: Transaction): Transaction {
  return {
    ...transaction,
    amount: {
      originalAmount: { ...transaction.amount.originalAmount },
      chargedAmount: { ...transaction.amount.chargedAmount },
    },
    description: { ...transaction.description },
    category: { ...transaction.category },
    changedCategory: transaction.changedCategory
      ? { ...transaction.changedCategory }
      : undefined,
    date: { ...transaction.date },
    rules: transaction.rules ? [...transaction.rules] : [],
  };
}

function prepareFromSnapshot() {
  // The open page stores the enrichment for the view the user is actually on.
  // Don't replace that with the default-view warmup.
  if (preparedEnrichment) return;
  const data = snapshot.data;
  if (!data?.transactions.length || typeof document === "undefined") return;

  const lang = readLanguageCookie(document.cookie);
  const expensesCategoryType = cashflowCharts[lang].CategoryTypes.Sub;
  const incomeCategoryType = cashflowCharts[lang].CategoryTypes.Sub;
  const selectedAccounts = readSelectedAccounts();
  const budgetMap =
    data.budgetGroups.length && data.budgetCategories.length
      ? buildBudgetCategoryMap(
          lang,
          data.budgetGroups,
          data.budgetCategories,
          data.budgetMappings
        )
      : undefined;
  const enriched = data.transactions.map(cloneTransaction);
  enrichTransactions(
    lang,
    enriched,
    expensesCategoryType,
    incomeCategoryType,
    readTransactionRules(),
    budgetMap
  );
  applyConnectedCardFilter(enriched, selectedAccounts);
  storePreparedEnrichment(
    {
      transactions: data.transactions,
      rules: readTransactionRules(),
      lang,
      expensesCategoryType,
      incomeCategoryType,
      accountKey: accountSelectionKey(selectedAccounts),
      budgetGroups: data.budgetGroups,
      budgetCategories: data.budgetCategories,
      budgetMappings: data.budgetMappings,
    },
    enriched
  );
}

function schedulePrepare() {
  if (typeof window === "undefined") return;
  const generation = ++prepareGeneration;
  const run = () => {
    if (generation !== prepareGeneration) return;
    try {
      prepareFromSnapshot();
    } catch (error) {
      console.error("Failed to prepare cashflow cache", error);
    }
  };
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(run, { timeout: 800 });
  } else {
    window.setTimeout(run, 1);
  }
}

/**
 * Update the in-memory snapshot after a fresh server payload.
 * Does not notify subscribers — the open page already holds this data in state.
 * The next visit reads it synchronously.
 */
export function rememberCashflowCache(patch: Partial<CashflowCachedData>) {
  if (!snapshot.ready || !snapshot.data) return;
  snapshot.data = { ...snapshot.data, ...patch };
  preparedEnrichment = null;
  schedulePrepare();
}

let preloadPromise: Promise<void> | null = null;

export function preloadCashflowClientCache(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (!preloadPromise) {
    preloadPromise = (async () => {
      try {
        const [transactions, budgetGroups, budgetCategories, budgetMappings] =
          await Promise.all([
            localforage.getItem<Transaction[]>("transactionData"),
            localforage.getItem<BudgetCategoryGroup[]>("budgetGroups"),
            localforage.getItem<BudgetCategory[]>("budgetCategories"),
            localforage.getItem<BudgetCategoryCodeMapping[]>("budgetMappings"),
          ]);
        snapshot = {
          ready: true,
          data: {
            transactions: Array.isArray(transactions) ? transactions : [],
            rules: readTransactionRules(),
            budgetGroups: Array.isArray(budgetGroups) ? budgetGroups : [],
            budgetCategories: Array.isArray(budgetCategories)
              ? budgetCategories
              : [],
            budgetMappings: Array.isArray(budgetMappings) ? budgetMappings : [],
          },
        };
      } catch {
        snapshot = { ready: true, data: emptyData() };
      }
      emit();
      schedulePrepare();
    })();
  }
  return preloadPromise;
}

if (typeof window !== "undefined") {
  void preloadCashflowClientCache();
}
